import TableRowActionMenu from '../BaseComponents/TableRowActionMenu';
import { useDispatch, useSelector } from 'react-redux';

import { formatInTimeZone } from 'date-fns-tz';
import './Appointments.scss';
import './GlobalAppointmentsTable.scss';
import { ReactComponent as FileIcon } from '../../assets/icons/File.svg';
import {
  AppointmentAssigneeType,
  AppScenariosAppointmentListAllAppointmentsApplicationPayloadAppointmentInResponse,
  AppointmentStatus,
  LocationType,
} from '../../types/apiGeneratedTypes';
import {
  setAppointmentIdToEdit,
  changeEditModalVisibility,
  changeDeleteModalVisibility,
  changeShowAppointmentTestResult,
  setAppointmentsList,
} from '../../store/appointmentsSlice';
import { NavLink } from 'react-router';
import { RootState } from '../../store/store';
import AppointmentInlineStatusSelect from './AppointmentInlineStatusSelect';

type Props = {
  appointmentData: AppScenariosAppointmentListAllAppointmentsApplicationPayloadAppointmentInResponse;
  doctorNameById?: Record<string, string>;
  nutritionistNameById?: Record<string, string>;
};

const AppointmentsTableRow = ({
  appointmentData,
  doctorNameById,
  nutritionistNameById,
}: Props) => {
  const dispatch = useDispatch();
  const { appointmentsList } = useSelector((state: RootState) => state.appointments);
  const assigneeType = appointmentData.assignee_type;
  const assigneeId = appointmentData.assignee_id;
  const assigneeName =
    assigneeType === AppointmentAssigneeType.DOCTOR && assigneeId
      ? doctorNameById?.[String(assigneeId)]
      : assigneeType === AppointmentAssigneeType.NUTRITIONIST && assigneeId
        ? nutritionistNameById?.[String(assigneeId)]
        : undefined;

  const membershipType = appointmentData.member?.membership_type
    ? appointmentData.member.membership_type.charAt(0).toUpperCase() +
      appointmentData.member.membership_type.slice(1).toLowerCase()
    : '—';

  const onOptionClick = (id: number, action: string) => {
    dispatch(setAppointmentIdToEdit(id));
    if (action === 'edit') return dispatch(changeEditModalVisibility(true));
    if (action === 'delete') return dispatch(changeDeleteModalVisibility(true));
  };

  return (
    <div className='table-row'>
      <div className='status'>
        <AppointmentInlineStatusSelect
          appointmentId={appointmentData.id}
          status={appointmentData.status}
          onStatusSaved={(newStatus: AppointmentStatus) => {
            dispatch(
              setAppointmentsList(
                appointmentsList.map((a) =>
                  a.id === appointmentData.id ? { ...a, status: newStatus } : a
                )
              )
            );
          }}
        />
      </div>
      <div className='name'>
        <NavLink
          to={`/members/${appointmentData?.member?.id}`}
          className='link'
        >
          {`${appointmentData?.member?.first_name} ${appointmentData?.member?.last_name}`}
        </NavLink>
      </div>
      <div className='date'>
        {formatInTimeZone(
          appointmentData.scheduled_time,
          'Europe/London',
          'dd/MM/yyyy'
        )}
      </div>
      <div className='time'>
        {formatInTimeZone(
          appointmentData.scheduled_time,
          'Europe/London',
          'hh:mm a'
        )}
      </div>
      <div className='test-names'>{appointmentData.title}</div>
      <div className='assignee'>
        {assigneeType && assigneeId ? (
          <div className='assignee-display'>
            <span className='assignee-type-tag'>{assigneeType}</span>
            <span className='assignee-name'>{assigneeName || '—'}</span>
          </div>
        ) : (
          '—'
        )}
      </div>
      <div className='membership-type'>{membershipType}</div>
      <div className='location'>
        {appointmentData.location === LocationType.ONLINE &&
        appointmentData.meeting_url ? (
          <div className='location-online'>
            <span className='location-tag'>{appointmentData.location}</span>
            <a
              className='meeting-link'
              href={appointmentData.meeting_url}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
            >
              Meeting Link
            </a>
          </div>
        ) : (
          <span className='location-tag'>{appointmentData.location}</span>
        )}
      </div>
      <div className='appointment-id'>{appointmentData.unique_id}</div>
      <div
        className='test-results'
        onClick={() => {
          dispatch(
            changeShowAppointmentTestResult({
              appointmentId: appointmentData.id,
              memberData: appointmentData.member
            })
          );
        }}
      >
        <FileIcon />
        View results
      </div>
      <div className='actions'>
        <TableRowActionMenu
          options={[
            { key: 'edit', value: 'Edit' },
            { key: 'delete', value: 'Delete' }
          ]}
          id={appointmentData.id}
          onActionMenuClick={onOptionClick}
        />
      </div>
    </div>
  );
};

export default AppointmentsTableRow;
